import { useState, createRef, useEffect } from 'react';
import { Image } from 'expo-image';
import { VStack, Heading, Text, ScrollView, HStack, Box } from 'native-base';

// Components
import {
  PrimaryButton,
  CustomCheckbox,
  ModalWrapper,
  Gradient,
  ErrorAndRefetch,
  TertiaryButton,
  IconButton,
} from '../../';
import { ActivityIndicator } from 'react-native';

// Assets
import { theme } from '@breakroom-nx/assets';
import diceImage from '../../../../assets/src/images/dice.webp';

// Redux
import { useDispatch } from 'react-redux';
import {
  closeModal,
  openModal,
  useAppDispatch,
  useGetVibesQuery,
  useAppSelector,
  getCreatingRoomState,
  useGetMoodsQuery,
  setVibe,
  clearVibe,
} from '@breakroom-nx/redux';

export const Randomizer = () => {
  const beginning = "I'm in the mood to talk about ";
  const dispatch = useDispatch();
  const appDispatch = useAppDispatch();

  const [topic, setTopic] = useState<string | undefined>(undefined);

  const myRef = createRef();

  useEffect(() => {
    appDispatch({ type: 'clearVibe' });
  }, []);

  const creatingRoom: boolean = useAppSelector(getCreatingRoomState);

  const {
    data: topics,
    isLoading: isLoadingTopics,
    error: errorGettingTopics,
    refetch: refetchTopics,
  } = useGetVibesQuery();

  // Render CustomCheckboxes for moods or topics
  const renderSelection = (
    selection: { title: string; icon?: string | undefined }[] | undefined,
    selectedValue: string | undefined,
    setSelectedValue: (val: string | undefined) => void
  ) => {
    return selection?.map((vibe) => (
      <CustomCheckbox
        key={vibe.title}
        title={vibe.title}
        checked={vibe.title === selectedValue}
        onCheckboxSelect={() => {
          setSelectedValue(
            vibe.title === selectedValue ? undefined : vibe.title
          );
        }}
        startIcon={
          <Box size={'35%'}>
            <Text bg={'transparent'} fontSize={25}>
              {vibe.icon}
            </Text>
          </Box>
        }
        otherSelected={!!selectedValue && vibe.title !== selectedValue}
      />
    ));
  };

  // Button text reflects the selected topic
  const getButtonText = () => {
    if (topic) {
      return 'Find Rooms';
    } else {
      return 'Select a Topic';
    }
  };

  const renderHeading = () => {
    return (
      <>
        {beginning}
        <Text color={theme.colors.primary[400]}>{topic}</Text>
      </>
    );
  };

  return (
    <ModalWrapper>
      <VStack
        bg={'surface.100'}
        p={7}
        rounded={'2xl'}
        width={'full'}
        alignItems={'center'}
        space={5}
      >
        <Heading fontSize={30} lineHeight={34} textAlign={'center'}>
          {renderHeading()}
        </Heading>

        {
          isLoadingTopics ? (
            <ActivityIndicator
              color={'white'}
              animating={true}
              hidesWhenStopped={true}
            />
          ) : (topics ?? []).length > 0 ? (
            <>
              <Gradient
                colors={[theme.colors.surface[100], 'transparent']}
                mb={-10}
              />
              <ScrollView
                showsVerticalScrollIndicator={true}
                maxHeight={400}
                _contentContainerStyle={{
                  width: 'full',
                  py: 4,
                }}
              >
                <HStack
                  direction="row"
                  flexWrap="wrap"
                  justifyContent={'center'}
                  alignItems={'center'}
                  space={3}
                  ref={myRef}
                >
                  {renderSelection(topics, topic, setTopic)}
                </HStack>
              </ScrollView>
              <Gradient
                colors={['transparent', theme.colors.surface[100]]}
                mt={-10}
              />
            </>
          ) : null
          // <ErrorAndRefetch
          //   onRefetch={refetchTopics()}
          //   error={'No Topics Found'}
          // />
        }
        <VStack
          position={'relative'}
          width={'full'}
          alignItems={'center'}
          justifyContent={'center'}
          space={5}
        >
          <PrimaryButton
            text={getButtonText()}
            disabled={isLoadingTopics || !topic}
            onPress={() => {
              const selectedTopic = topics?.find((t) => t.title === topic);
              // Push both to the slice
              appDispatch(
                setVibe(
                  selectedTopic
                    ? {
                        text: selectedTopic.title,
                        icon: selectedTopic.icon ?? '',
                      }
                    : { text: '', icon: '' }
                )
              );
              dispatch(closeModal());
              dispatch(
                openModal({
                  modalType: creatingRoom ? 'CreateChat' : 'Search',
                })
              );
            }}
          />
          <IconButton
            onPress={() => {
              const choices = topics;
              if (choices && choices.length > 0) {
                const randomIndex = Math.floor(Math.random() * choices.length);
                const randomVibe = choices[randomIndex];

                if (randomVibe) {
                  setTopic(randomVibe.title);
                }
              }
            }}
            tooltipText={'Randomize'}
            icon={
              <Image
                source={{ uri: diceImage }}
                cachePolicy={'memory-disk'}
                accessibilityLabel={`Dice Image`}
                contentFit={'contain'}
                style={{
                  width: '20px',
                  aspectRatio: 1,
                }}
              />
            }
          />
        </VStack>
      </VStack>
    </ModalWrapper>
  );
};
